/* eslint-disable no-param-reassign */

// Third-party
import React, { PureComponent } from 'react';
import Highcharts, {
  SeriesColumnOptions,
  YAxisOptions,
  YAxisLabelsOptions,
  FormatterCallbackFunction,
  AxisLabelsFormatterContextObject,
  PlotOptions,
  PlotColumnOptions,
  DataLabelsOptionsObject,
  DataLabelsFormatterCallbackFunction,
} from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import classNames from 'classnames';
import chroma from 'chroma-js';

import * as ProposalNS from '../../Proposal';

// Utils
import { convertConfigOptionsToHighcharts } from '../../../../utils/charts';
import { formatLocalizedString, LOCALE } from '../../../../utils/i18n';
import { ProposalItemLabelCell } from '@/views/components/cells';

import Nullable from '@/types/nullable';
import Dictionary from '@/types/dictionary';
import SeriesData from '@/types/series-data';
import Locale from '@/types/locale';
import ComparisonItem from '@/types/comparison-item';
import LocalizedString from '@/types/localized-string';
import AccessibilityDescription from '../misc/AccessibilityDescription';

// Creating type aliases to centralize Highcharts dependency in ProposalBarChart.tsx
export type BarChartOptions = Highcharts.Options;

const enDefaultFormatter = {
  formatter() {
    const self: any = this;
    let v: string = self.value.toFixed(1);
    v = v.replace('.0', '');
    return v;
  },
};

const frDefaultFormatter = {
  formatter() {
    const self: any = this;
    let v: string = self.value.toFixed(1);
    v = v.replace('.0', '').replace(',', ' ').replace('.', ',');
    return v;
  },
};

const enPercentageFormatter = {
  formatter() {
    const self: any = this;
    let v: string = self.value.toFixed(1);
    v = v.replace('.0', '');
    return `${v}%`;
  },
};

const frPercentageFormatter = {
  formatter() {
    const self: any = this;
    let v: string = self.value.toFixed(1);
    v = v.replace('.0', '').replace(',', ' ').replace('.', ',');
    return `${v} %`;
  },
};

const enPercentageFormatterY = {
  formatter() {
    const self: any = this;
    let v: string = self.y.toFixed(1);
    v = v.replace('.0', '');
    return `${v}%`;
  },
};

const frPercentageFormatterY = {
  formatter() {
    const self: any = this;
    let v: string = self.y.toFixed(1);
    v = v.replace('.0', '').replace(',', ' ').replace('.', ',');
    return `${v} %`;
  },
};

type Props = {
  data: SeriesData[][];
  className?: string;
  dark?: boolean;
  showHeader?: boolean;
  colorsDict: Nullable<Dictionary<string>>;
  configOptions: Partial<ProposalNS.BarChartConfigOptions>;
  valueFormatter?: ProposalNS.ValueFormatter;
  locale?: Locale;
  proposalItems: ComparisonItem[];
  showBaseCurrency?: boolean;
  accessibilityDescription?: LocalizedString;
};

type State = {
  chartOptions: BarChartOptions;
};

class ProposalBarChart extends PureComponent<Props, State> {
  private defaultColorScale = chroma.scale(['#0e4263', '#0587da', '#11e9f7', '#f34190', '#f5994c', 'yellow', '#157c55']);

  static defaultProps: Props = {
    colorsDict: null,
    configOptions: {},
    data: [],
    showHeader: false,
    proposalItems: [],
  };

  readonly state: State = {
    chartOptions: convertConfigOptionsToHighcharts('column', this.props.configOptions, this.props.locale),
  };

  componentDidMount() {
    this.updateChart();
  }

  componentDidUpdate(prevProps: Props) {
    const { data } = this.props;

    if (prevProps.data !== data) {
      this.updateChart();
    }
  }

  getDefaultOptions = () => ({
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      inverted: false,
    },
    xAxis: {
      type: 'category',
      labels: {
        rotation: 0,
      },
      crosshair: {
        width: 0,
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: '',
      },
      labels: {
        autoRotation: false,
      },
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        shadow: false,
        states: {
          hover: {
            enabled: false,
          },
        },
      },
    },
    title: undefined,
    credits: { enabled: false },
    tooltip: {
      enabled: false,
    },
  });

  getDataColor = (data: SeriesData[], index: number) => {
    const { colorsDict } = this.props;

    if (colorsDict) {
      const datum = data[index];

      if (datum.key && colorsDict[datum.key]) {
        return colorsDict[datum.key];
      }

      if (datum.name && colorsDict[datum.name]) {
        return colorsDict[datum.name];
      }

      if (colorsDict[index]) {
        return colorsDict[index];
      }
    }

    return this.defaultColorScale(index / data.length).hex();
  };

  getSeriesData = (data: SeriesData[][] = []): SeriesColumnOptions[] => {
    const series: SeriesColumnOptions[] = data.map(instance => {
      const chartData = instance.map((datum, i) => {
        const color = this.getDataColor(instance, i);
        return {
          y: datum.y,
          name: datum.name,
          color,
        };
      });

      return {
        type: 'column',
        data: chartData,
      };
    });

    return series;
  };

  updateChart = () => {
    const { data } = this.props;
    const { chartOptions } = this.state;

    const series = this.getSeriesData(data);
    const defaultOptions = this.getDefaultOptions();
    const configuredOptions = { ...defaultOptions, ...chartOptions } as BarChartOptions;
    const updatedOptions = { ...configuredOptions, series } as BarChartOptions;

    this.setState({ chartOptions: this.applyFormatter(updatedOptions) });
  };

  applyFormatter = (options: BarChartOptions) => {
    const { valueFormatter, locale } = this.props;

    if (valueFormatter !== undefined) {
      const formatter =
        locale === LOCALE.EN_CA
          ? valueFormatter === ProposalNS.ValueFormatter.PERCENTAGE
            ? enPercentageFormatter.formatter
            : enDefaultFormatter.formatter
          : valueFormatter === ProposalNS.ValueFormatter.PERCENTAGE
            ? frPercentageFormatter.formatter
            : frDefaultFormatter.formatter;

      if (options.yAxis === undefined) {
        options.yAxis = {} as YAxisOptions;
      }
      const localYAxis: YAxisOptions = options.yAxis as YAxisOptions;
      if (localYAxis.labels === undefined) {
        localYAxis.labels = {} as YAxisLabelsOptions;
      }
      localYAxis.labels.formatter = formatter as FormatterCallbackFunction<AxisLabelsFormatterContextObject>;

      if (options.plotOptions === undefined) {
        options.plotOptions = {} as PlotOptions;
      }
      if (options.plotOptions.column === undefined) {
        options.plotOptions.column = {} as PlotColumnOptions;
      }
      if (options.plotOptions.column.dataLabels === undefined) {
        options.plotOptions.column.dataLabels = {} as DataLabelsOptionsObject;
      }

      options.plotOptions.column.dataLabels.formatter = (
        locale === LOCALE.EN_CA ? enPercentageFormatterY.formatter : frPercentageFormatterY.formatter
      ) as DataLabelsFormatterCallbackFunction;
    }

    return options;
  };

  renderHeader = () => {
    const { locale, showHeader, proposalItems, showBaseCurrency } = this.props;

    if (!showHeader || proposalItems.length === 0) {
      return null;
    }

    return (
      <div className="proposal-legend bar-chart-header">
        {proposalItems.map(item => (
          <ProposalItemLabelCell
            value={formatLocalizedString(item.name, locale)}
            key={item._id}
            color={item.color}
            itemType={item.type}
            clamp={1}
            flags={item.flags}
            currency={showBaseCurrency ? item.currency : undefined}
          />
        ))}
      </div>
    );
  };

  getDataItems(itemsData?: ComparisonItem[]) {
    const { locale } = this.props;
    return (itemsData?.map(item => item.name[locale || LOCALE.EN_CA] || '') as string[]) || [];
  }

  render() {
    const { dark, className, accessibilityDescription, proposalItems, locale } = this.props;
    const { chartOptions } = this.state;

    return (
      <div id="chart" className={classNames('bar-chart', className, { dark })}>
        {this.renderHeader()}

        {accessibilityDescription && (
          <AccessibilityDescription description={accessibilityDescription} items={this.getDataItems(proposalItems)} locale={locale} />
        )}

        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      </div>
    );
  }
}

export type BarChartProps = Props;

export default ProposalBarChart;
